import React, { useState, useEffect, useRef } from "react";
import "./Dashboard.scss";
import getSymbolFromCurrency from "currency-symbol-map";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import { useAppSelector, useAppDispatch } from "store/index";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import dashboardIcon from "assets/icons/sidebar/dashboard.svg";
import arpuIcon from "assets/images/dashboard/arpu.svg";
import ftdIcon from "assets/images/dashboard/ftd.svg";
import ftdAlarmIcon from "assets/images/dashboard/clock.svg";
import coinsIcon from "assets/images/dashboard/sum.svg";
import coinsAlarmIcon from "assets/images/dashboard/clock.svg";
import turnoverIcon from "assets/images/dashboard/turnover.svg";
import signUpIcon from "assets/images/dashboard/sign.svg";
import downIcon from "assets/images/dashboard/arrowDown.svg";
import upIcon from "assets/images/dashboard/arrowUp.svg";
import light from "assets/images/dashboard/light.svg";
import bgRed from "assets/images/dashboard/backgroundRed.png";
import bgGreen from "assets/images/dashboard/backgroundGreen.png";
import downIconG from "assets/images/dashboard/gDown.svg";
import upIconG from "assets/images/dashboard/gUp.svg";
import patternG from "assets/images/dashboard/patternG.png";
import patternB from "assets/images/dashboard/patternB.png";
import patternY from "assets/images/dashboard/patternY.png";
import userBg from "assets/images/dashboard/userBg.png";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DashboardSkeleton from "./DashboardSkeleton";
import { currencySymbol } from "configs/brands";
import ImageWithFallback from "helpers/ImageWithFallback";

interface DashboardData {
  registrationCount: number;
  loginCount: number;
  betFrequency: number;
  activeUsersCount: number;
  ggrTotal: number;
  ggrReal: number;
  ggrBonus: number;
  maxWinBonus: number;
  averageWinBonus: number;
  minBetBonus: number;
  averageBetBonus: number;
  turnoverBonus: number;
  maxWinReal: number;
  averageWinReal: number;
  turnoverReal: number;
  minBetReal: number;
  maxBetReal: number;
  maxBetBonus: number;
  averageBetReal: number;
  betRealCount: number;
  betBonusCount: number;
  winRealCount: number;
  winBonusCount: number;
  topGamesSum: {
    gameId?: number;
    title: string;
    image: string;
    value: number;
  }[];
  topGamesCount: {
    gameId?: number;
    title: string;
    image: string;
    value: number;
  }[];
  turnoverTotal: number;
  freeSpinsTotal: number;
  freeSpinsPlayed: number;
  sumFTD: number;
  countFTD: number;
  averageFTD: number;
  sumReDeposits: number;
  countReDeposits: number;
  averageReDeposits: number;
  freeSpinsPlayedSum: number;
  freeSpinsLeft: number;
  ARPPU: number | null;
}

const dashboardDefaults: DashboardData = {
  registrationCount: 0,
  loginCount: 0,
  betFrequency: 0,
  activeUsersCount: 0,
  ggrTotal: 0,
  ggrReal: 0,
  ggrBonus: 0,
  maxWinBonus: 0,
  averageWinBonus: 0,
  minBetBonus: 0,
  averageBetBonus: 0,
  turnoverBonus: 0,
  maxWinReal: 0,
  averageWinReal: 0,
  turnoverReal: 0,
  minBetReal: 0,
  maxBetReal: 0,
  maxBetBonus: 0,
  averageBetReal: 0,
  betRealCount: 0,
  betBonusCount: 0,
  winRealCount: 0,
  winBonusCount: 0,
  turnoverTotal: 0,
  freeSpinsTotal: 0,
  freeSpinsPlayed: 0,
  sumFTD: 0,
  countFTD: 0,
  averageFTD: 0,
  sumReDeposits: 0,
  countReDeposits: 0,
  averageReDeposits: 0,
  freeSpinsPlayedSum: 0,
  freeSpinsLeft: 0,
  ARPPU: null,
  topGamesSum: [],
  topGamesCount: [],
};

const normalizeDashboard = (payload: Partial<DashboardData>): DashboardData => {
  const normalized = { ...dashboardDefaults, ...payload };
  for (const key of Object.keys(dashboardDefaults) as (keyof DashboardData)[]) {
    if (key === "topGamesSum" || key === "topGamesCount") {
      normalized[key] = Array.isArray(payload[key])
        ? payload[key]!.map((game) => ({
            ...game,
            value: Number.isFinite(Number(game.value)) ? Number(game.value) : 0,
          }))
        : [];
    } else if (key === "ARPPU") {
      const value = payload.ARPPU;
      normalized.ARPPU = value != null && Number.isFinite(Number(value)) ? Number(value) : null;
    } else {
      const value = Number(payload[key]);
      normalized[key] = Number.isFinite(value) ? value : 0;
    }
  }
  return normalized;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels,
);

const ggrData = (real: number, bonus: number) => ({
  labels: ["Bonus", "Real"],
  datasets: [
    {
      data: [bonus, real],
      backgroundColor: ["#4EC040", "#13452D"],
      borderRadius: 10,
      borderWidth: 0,
    },
  ],
});

const fsData = (fsLeft: number, fsPlayed: number) => {
  return {
    labels: ["FS Played", "FS Left"],
    datasets: [
      {
        data: [fsPlayed, fsLeft],
        backgroundColor: ["#4EC040", "#13452D"],
        borderRadius: 10,
        borderWidth: 0,
      },
    ],
  };
};

const doughnutOptions = {
  cutout: "70%",
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      formatter: (value: any) => {
        if (value >= 1_000) {
          return (value / 1_000)?.toFixed(1)?.replace(/\.0$/, "") + "к";
        }
        return value;
      },
      color: "#fff",
      font: {
        weight: "bold" as "bold",
        size: 16,
      },
    },
  },
};

const doughnutOptions2 = {
  rotation: -90,
  circumference: 180,
  cutout: "60%",
  plugins: {
    legend: {
      display: false,
    },
    datalabels: {
      formatter: (value: any) => {
        if (value >= 1_000) {
          return (value / 1_000)?.toFixed(1)?.replace(/\.0$/, "") + "к";
        }
        return value;
      },
      color: "#fff",
      font: {
        weight: "bold" as "bold",
        size: 16,
      },
    },
  },
};

const COLOR_BY_LABEL: Record<string, { bg: string; br: string }> = {
  "Casino – Real": { bg: "rgba(96,165,250,0.85)", br: "rgba(96,165,250,1)" },
  "Casino – Bonus": { bg: "rgba(167,139,250,0.85)", br: "rgba(167,139,250,1)" },
  "Sport – Real": { bg: "rgba(251,146,60,0.85)", br: "rgba(251,146,60,1)" },
  "Sport – Bonus": { bg: "rgba(250,204,21,0.85)", br: "rgba(250,204,21,1)" },
};

const Dashboard: React.FC = () => {
  // ----
  const gameOpeningEnabled = true;
  const { t } = useTranslation();

  const isLogged = useAppSelector((store) => store.auth.isAuthorized);
  const baseCurrency = useAppSelector((store) => store.currencies.baseCurrency);
  const baseCurrencySymbol = baseCurrency ? (getSymbolFromCurrency(baseCurrency) ?? currencySymbol) : currencySymbol;
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(new Date());
  const [data, setData] = useState<DashboardData>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gamePermissions = usePermissionHelper("/game-center/games/active");

  const openGameById = (id: number) => {

    const params = new URLSearchParams(location.search);
    params.set("activeModal", ModalTypes.editGame);
    params.set("entityId", String(id));
    params.set("tab", "main");
    navigate(`${location.pathname}?${params.toString()}`);
    dispatch(openModal({ activeModal: ModalTypes.editGame }));
  };

  const handleOpenGame = (game: DashboardData["topGamesCount"][number]) => {
    if (!gamePermissions?.read) return;
    if (typeof game.gameId === "number" && Number.isInteger(game.gameId) && game.gameId > 0) {
      openGameById(game.gameId);
      return;
    }
    dispatch(showNotification({
      type: NotificationTypes.error,
      title: t("Game"),
      message: t("Unable to open this game: game ID is missing."),
    }));
  };
  const requestId = useRef(0);
  const [turnoverGraph, setTurnoverGraph] = useState<any | null>(null);

  const handleLoadData = async () => {
    const currentRequest = ++requestId.current;
    try {
      const result = await instance.get(
        `${apiRoutes.dashboardData}?dataFrom=${moment(dateFrom).format("DD.MM.YYYY")}&dataTo=${moment(dateTo).format("DD.MM.YYYY")}`,
      );
      if (currentRequest !== requestId.current) return;
      const graph = result.data?.graph;

      let normalizedGraph = null;
      if (graph?.data?.labels?.length && Array.isArray(graph.data.datasets)) {
        const labels = graph.data.labels;
        const isDesc =
          new Date(labels[0]).getTime() >
          new Date(labels[labels.length - 1]).getTime();

        normalizedGraph = {
          ...graph,
          data: {
            ...graph.data,
            labels: isDesc ? [...labels].reverse() : [...labels],
            datasets: graph.data.datasets.map((d: any) => ({
              ...d,
              data: (Array.isArray(d.data) ? (isDesc ? [...d.data].reverse() : [...d.data]) : []).map(
                (value: unknown) => Number.isFinite(Number(value)) ? Number(value) : 0,
              ),
            })),
          },
        };
      }

      setData(normalizeDashboard(result.data ?? {}));
      setTurnoverGraph(normalizedGraph);
    } catch {
      if (currentRequest !== requestId.current) return;
      setData(undefined);
      setTurnoverGraph(null);
    }
  };

  useEffect(() => {
    if (isLogged) {
      handleLoadData();
    }
    return () => { requestId.current += 1; };
  }, [dateFrom, dateTo, isLogged]);

  const isTurnoverEmpty = () => !turnoverGraph?.data?.labels?.length;

  return (
    <div className="dashboard-wrapper">
      {data && (
        <div className="dashboard-list-header">
          <div className="dashboard-filter">
            <div className="icon">
              <img src={dashboardIcon} alt="" />
            </div>
            <div className="row">
              <div className="dashboard-date">
                <span className="label">{t("from")}:</span>
                <div className="date-picker-wrapper">
                  <button
                    onClick={() =>
                      setDateFrom(moment(dateFrom).subtract(1, "days").toDate())
                    }
                  >
                    &lt;
                  </button>
                  <DatePicker
                    customInput={<ReadOnlyInput />}
                    selected={dateFrom}
                    onChange={(date: any) => setDateFrom(date)}
                    dateFormat={"d/MM/YYYY"}
                    maxDate={dateTo}
                  />
                  <button
                    onClick={() =>
                      setDateFrom(moment(dateFrom).add(1, "days").toDate())
                    }
                  >
                    &gt;
                  </button>
                </div>
              </div>
              <div className="dashboard-date">
                <span className="label">{t("to")}:</span>
                <div className="date-picker-wrapper">
                  <button
                    onClick={() =>
                      setDateTo(moment(dateTo).subtract(1, "days").toDate())
                    }
                  >
                    &lt;
                  </button>
                  <DatePicker
                    customInput={<ReadOnlyInput />}
                    selected={dateTo}
                    onChange={(date: any) => setDateTo(date)}
                    dateFormat={"d/MM/YYYY"}
                  />
                  <button
                    onClick={() =>
                      setDateTo(moment(dateTo).add(1, "days").toDate())
                    }
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="dasboard-content">
        {data ? (
          <>
            <div className="dasboard-cards">
              <div className="item">
                <div className="header">
                  <div className="title">
                    <span>{t("sumCounts")}</span>
                    <div className="label">{t("average")}</div>
                    <div className="data">
                      {data.sumFTD} / {data.countFTD}
                    </div>
                  </div>
                  <img src={ftdIcon} alt="" />
                </div>
                <div className="footer">
                  <div className="content">
                    <img src={ftdAlarmIcon} alt="" />
                    <span style={{ color: "#4EC040" }}>{data.averageFTD}</span>
                  </div>
                </div>
                <img src={bgGreen} alt="" className="bg" />
                <img src={upIconG} alt="" className="graph" />
              </div>

              <div className="item">
                <div className="header">
                  <div className="title">
                    <span>{t("sumCounts")}</span>
                    <div className="label">{t("average")}</div>
                    <div className="data">
                      {data.sumReDeposits} / {data.countReDeposits}
                    </div>
                  </div>
                  <img src={coinsIcon} alt="" />
                </div>
                <div className="footer">
                  <div className="content">
                    <img src={coinsAlarmIcon} alt="" />
                    <span style={{ color: "#4EC040" }}>
                      {data.averageReDeposits}
                    </span>
                  </div>
                </div>
                <img src={bgGreen} alt="" className="bg" />
                <img src={upIconG} alt="" className="graph" />
              </div>

              <div className="item">
                <div className="header">
                  <div className="title">
                    <span>{t("totalTurnover")}</span>
                    <div className="label">{t("downFromYday")}</div>
                    <div className="data">
                      {baseCurrencySymbol}
                      {data.turnoverTotal}
                    </div>
                  </div>
                  <img src={turnoverIcon} alt="" />
                </div>
                <div className="footer">
                  <div className="content">
                    <img src={downIcon} alt="" />
                    <span style={{ color: "#DD172C" }}>4.3%</span>
                  </div>
                </div>
                <img src={bgRed} alt="" className="bg" />
                <img src={downIconG} alt="" className="graph" />
              </div>

              <div className="item">
                <div className="header">
                  <div className="title">
                    <span>{t("signUpSignIn")}</span>
                    <div className="label">{t("upFromYday")}</div>
                    <div className="data">
                      {data.registrationCount} / {data.loginCount}
                    </div>
                  </div>
                  <img src={signUpIcon} alt="" />
                </div>
                <div className="footer">
                  <div className="content">
                    <img src={upIcon} alt="" />
                    <span style={{ color: "#4EC040" }}>1.8%</span>
                  </div>
                </div>
                <img src={bgGreen} alt="" className="bg" />
                <img src={upIconG} alt="" className="graph" />
              </div>

              <div className="item">
                <div className="header">
                  <div className="title">
                    <span>ARPPU</span>
                    <div className="data">
                      {data.ARPPU == null ? "/" : `${baseCurrencySymbol}${data.ARPPU.toFixed(2)}`}
                    </div>
                  </div>
                  <img src={arpuIcon} alt="" />
                </div>
                <img src={bgGreen} alt="" className="bg" />
              </div>
            </div>
            <div className="dasboard-content--row">
              <div className="left">
                <div className="dashboard-charts-wrapper">
                  <div className="header">
                    <div className="content">
                      {`${t("hourlyBetTurnover")} (${baseCurrencySymbol})`}
                    </div>
                  </div>

                  <div className="g-wrap">
                    {isTurnoverEmpty() ? (
                      <div className="empty">
                        {t("noDataForSelectedPeriod")}
                      </div>
                    ) : (
                      <Bar
                        data={{
                          ...turnoverGraph.data,
                          datasets: turnoverGraph.data.datasets.map(
                            (d: any) => {
                              const c = COLOR_BY_LABEL[d.label];
                              return {
                                ...d,
                                backgroundColor: c?.bg,
                                borderColor: c?.br,
                                borderWidth: 2,
                                borderRadius: {
                                  topLeft: 2,
                                  topRight: 2,
                                  bottomLeft: 0,
                                  bottomRight: 0,
                                },
                                borderSkipped: false,
                              };
                            },
                          ),
                        }}
                        options={{
                          ...turnoverGraph.options,
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            ...turnoverGraph.options?.scales,
                            x: {
                              ...turnoverGraph.options?.scales?.x,
                              ticks: {
                                ...turnoverGraph.options?.scales?.x?.ticks,
                                color: "#fff",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: (_: any, index: number) =>
                                  moment
                                    .utc(turnoverGraph.data.labels[index])
                                    .format("HH"),
                              },
                              grid: {
                                color: "rgba(255,255,255,0.1)",
                              },
                            },
                            y: {
                              ...turnoverGraph.options?.scales?.y,
                              ticks: {
                                ...turnoverGraph.options?.scales?.y?.ticks,
                                color: "#fff",
                                callback: (value: number) =>
                                  `${value} ${baseCurrencySymbol}`,
                              },
                              grid: {
                                color: "rgba(255,255,255,0.1)",
                              },
                            },
                          },

                          plugins: {
                            ...turnoverGraph.options?.plugins,

                            legend: {
                              display: true,
                              position: "bottom",
                              align: "center",

                              labels: {
                                boxWidth: 20,
                                boxHeight: 8,
                                generateLabels: (chart) =>
                                  chart.data.datasets.map((ds, i) => ({
                                    text:
                                      ds.label === "Casino – Real"
                                        ? t("legend.casinoReal")
                                        : ds.label === "Casino – Bonus"
                                          ? t("legend.casinoBonus")
                                          : ds.label === "Sport – Real"
                                            ? t("legend.sportReal")
                                            : t("legend.sportBonus"),
                                    fillStyle: ds.backgroundColor as string,
                                    strokeStyle: ds.borderColor as string,
                                    lineWidth: 1,
                                    hidden: !chart.isDatasetVisible(i),
                                    datasetIndex: i,
                                    fontColor: "#fff",
                                  })),
                              },
                            },
                            tooltip: {
                              ...turnoverGraph.options?.plugins?.tooltip,
                              mode: "index",
                              intersect: false,
                              titleColor: "#fff",
                              bodyColor: "#fff",
                            },

                            datalabels: {
                              anchor: "end",
                              align: "top",
                              offset: -5,
                              formatter: (value: any) =>
                                value > 0
                                  ? value >= 1000
                                    ? `${(value / 1000).toFixed(1)}к`
                                    : value
                                  : null,
                              font: { weight: "bold" },
                              color: "#fff",
                            },
                          },
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="dashboard-doughnut-charts">
                  <div className="doughnut-chart-container ggr">
                    <div className="name">GGR Data</div>
                    <div className="ggr-wrapper">
                      <Doughnut
                        data={ggrData(data.ggrReal, data.ggrBonus)}
                        options={doughnutOptions2}
                      />
                    </div>
                    <div className="ggr-info">
                      <div className="total">
                        {data.ggrTotal >= 1_000
                          ? (data.ggrTotal / 1_000)
                              ?.toFixed(1)
                              ?.replace(/\.0$/, "") + "к"
                          : data.ggrTotal.toFixed(2)}
                        <span>GGR total</span>
                      </div>
                      <div className="legends">
                        <div className="item">
                          GGR bonus <br /> balance
                        </div>
                        <div className="item">
                          GGR real <br /> balance
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="doughnut-chart-container fs">
                    <div className="name">FS Data</div>
                    <div className="fs-wrapper">
                      <Doughnut
                        data={fsData(data.freeSpinsLeft, data.freeSpinsPlayed)}
                        options={doughnutOptions}
                      />
                    </div>
                    <img src={light} alt="" />
                    <div className="fs-info">
                      <div className="total">
                        {data.freeSpinsTotal >= 1_000
                          ? (data.freeSpinsTotal / 1_000)
                              ?.toFixed(1)
                              ?.replace(/\.0$/, "") + "к"
                          : data.freeSpinsTotal?.toFixed(2)}
                      </div>
                      <div className="legends">
                        <div className="item">FS left </div>
                        <div className="item">FS played</div>
                      </div>
                      <div className="total-amount">
                        <span>Total win amount</span>
                        {baseCurrencySymbol}
                        {data.freeSpinsPlayedSum?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="right">
                <div className="info-wrap">
                  <div className="stats-block">
                    <div className="stats-item green">
                      <div className="content">
                        <span>Real</span>
                        {baseCurrencySymbol}
                        {data.averageBetReal}
                      </div>
                      <img src={patternG} alt="" />
                      <div className="title">BETs Average</div>
                      <div className="content">
                        <span>Bonus</span>
                        {baseCurrencySymbol}
                        {data.averageBetBonus}
                      </div>
                    </div>
                    <div className="stats-item green">
                      <div className="content">
                        <span>Real</span>
                        {baseCurrencySymbol}
                        {data.averageWinReal}
                      </div>
                      <img src={patternG} alt="" />
                      <div className="title">Wins Average</div>
                      <div className="content">
                        <span>Bonus</span>
                        {baseCurrencySymbol}
                        {data.averageWinBonus}
                      </div>
                    </div>
                    <div className="stats-item yellow">
                      <div className="content">
                        <span>Real</span>
                        {baseCurrencySymbol}
                        {data.maxBetReal}
                      </div>
                      <img src={patternY} alt="" />
                      <div className="title">bets max</div>
                      <div className="content">
                        <span>Bonus</span>
                        {baseCurrencySymbol}
                        {data.maxBetBonus}
                      </div>
                    </div>
                    <div className="stats-item yellow">
                      <div className="content">
                        <span>Real</span>
                        {baseCurrencySymbol}
                        {data.maxWinReal}
                      </div>
                      <img src={patternY} alt="" />
                      <div className="title">Wins Max</div>
                      <div className="content">
                        <span>Bonus</span>
                        {baseCurrencySymbol}
                        {data.maxWinBonus}
                      </div>
                    </div>
                    <div className="stats-item blue">
                      <div className="content">
                        <span>Real</span>
                        {data.betRealCount}
                      </div>
                      <img src={patternB} alt="" />
                      <div className="title">Bets Total</div>
                      <div className="content">
                        <span>Bonus</span>
                        {data.betBonusCount}
                      </div>
                    </div>
                    <div className="stats-item blue">
                      <div className="content">
                        <span>Real</span>
                        {data.winRealCount}
                      </div>
                      <img src={patternB} alt="" />
                      <div className="title">wins Total</div>
                      <div className="content">
                        <span>Bonus</span>
                        {data.winBonusCount}
                      </div>
                    </div>
                  </div>
                  <div className="users-block">
                    <img src={userBg} alt="" />
                    <div className="content">
                      <span>Active Users</span>
                      {data.activeUsersCount}
                    </div>
                    <div className="content">
                      <span>Bet Frequency</span>
                      {data.betFrequency}
                    </div>
                  </div>
                </div>
                <div className="games-wrap">
                  <div className="games-list">
                    <div className="header">Bet Leaders</div>
                    {data?.topGamesCount?.map((el) => (
                      <button className="item" type="button" key={`${el.title}-${el.value}`}
                        onClick={() => handleOpenGame(el)} disabled={!gameOpeningEnabled || !gamePermissions?.read}
                        aria-label={`${t("Open game")}: ${el.title}`}>
                        <ImageWithFallback src={el.image} alt="" />
                        <span className="title">{el.title}</span>
                        <span className="value">{el.value}</span>
                      </button>
                    ))}
                  </div>
                  <div className="games-list">
                    <div className="header">High Rollers</div>
                    {data?.topGamesSum?.map((el) => (
                      <button className="item" type="button" key={`${el.title}-${el.value}`}
                        onClick={() => handleOpenGame(el)} disabled={!gameOpeningEnabled || !gamePermissions?.read}
                        aria-label={`${t("Open game")}: ${el.title}`}>
                        <ImageWithFallback src={el.image} alt="" />
                        <span className="title">{el.title}</span>
                        <span className="value">
                          {baseCurrencySymbol}
                          {el.value}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <DashboardSkeleton />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
